import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {userSettingsApi} from 'api';
import {ExternalIntegrationPurposeSelect, PredefinedNotifierType} from 'components';
import {UserContext} from 'context';
import {AiProvider, ExternalIntegrationPurpose} from 'enums';
import {useApiData} from 'hooks';

import {AddToLiveHelp} from './AddToLiveHelp';
import {AdvancedSettings} from './AdvancedSettings';
import {OpenAIProperties} from './OpenAIProperties';
import {Promts} from './Promts';
import {AdvancedSettingsFields, FormFields, OpenAIPropertiesFields, PromtsFields, SectionName} from './types';

export const ChatGPT = () => {
    const {settings: {providerPurposes}} = useContext(UserContext);

    const [integrationPurpose, setIntegrationPurpose] = useState<ExternalIntegrationPurpose>(ExternalIntegrationPurpose.ClientTextChat);
    const [initialValues, setInitialValues] = useState<FormFields>({
        openAIProperties: {
            apiKey: '',
            enabled: false
        },
        advancedSettings: {
            chatCompletionParams: {
                model: '',
            }
        },
        promts: {
            knowledgeBase: '',
            botInstruction: '',
            suggestionInstruction: '',
        },
        actions: {
            callingClassificationInstruction: ''
        }
    });

    const purposes = useMemo(() => providerPurposes[AiProvider.ChatGpt] || [], [providerPurposes]);

    const {send: fetchSettings, data: settings, loading: fetchLoading} =
        useApiData(userSettingsApi.getChatGpt, {notificationType: PredefinedNotifierType.FetchError});

    const update = useCallback((section, data) =>
        userSettingsApi.updateChatGpt(integrationPurpose, data).then((result) => ({section, result})), [integrationPurpose]);

    const updateInitialValues = useCallback(
        ({section, result}) => setInitialValues((prev) =>
            ({...prev, [section]: result[section]})
        ),
        [setInitialValues]);

    const {send: updateSettings, loading: updateLoading} =
        useApiData(update, {
            notificationType: PredefinedNotifierType.Save,
            onSuccess: updateInitialValues
        });

    const handleSubmit = useCallback((
        section: SectionName,
        values: OpenAIPropertiesFields | AdvancedSettingsFields | PromtsFields
    ) => {
        updateSettings(section, {
            chatGpt: {
                [section]: values
            }
        });
    }, [updateSettings]);

    const loading = useMemo(() => fetchLoading || updateLoading, [fetchLoading, updateLoading]);

    useEffect(() => {
        fetchSettings(integrationPurpose);
    }, [fetchSettings, integrationPurpose]);

    useEffect(() => {
        setInitialValues({
            openAIProperties: settings?.openAIProperties || {
                enabled: false,
                apiKey: ''
            },
            advancedSettings: settings?.advancedSettings || {
                chatCompletionParams: {
                    model: '',
                }
            },
            promts: settings?.promts || {
                knowledgeBase: '',
                botInstruction: '',
                suggestionInstruction: '',
            },
            actions: settings?.actions || {
                callingClassificationInstruction: ''
            }
        });
    }, [settings]);

    return (
        <Fragment>
            {integrationPurpose === ExternalIntegrationPurpose.ClientTextChat &&
                <AddToLiveHelp settings={initialValues} />}
            <ExternalIntegrationPurposeSelect
                value={integrationPurpose}
                purposes={purposes}
                className='tw-mb-4 tw-max-w-104'
                onChange={setIntegrationPurpose}
            />
            <OpenAIProperties
                initialValues={initialValues.openAIProperties}
                loading={loading}
                onSubmit={(values) => handleSubmit(SectionName.OpenAIProperties, values)}
            />
            <Promts
                initialValues={initialValues.promts}
                loading={loading}
                onSubmit={(values) => handleSubmit(SectionName.Promts, values)}
            />
            <AdvancedSettings
                initialValues={initialValues.advancedSettings}
                loading={loading}
                onSubmit={(values) => handleSubmit(SectionName.AdvancedSettings, values)}
            />
        </Fragment>
    );
};